/**
 * Die Gegenstelle einer Nachricht — die Adresse der anderen Seite.
 *
 * Der Anlass: Zuordnen ging überall vom Absender aus. Bei eingehender Post
 * stimmt das, im Gesendet-Ordner steht dort aber die eigene Adresse. Wer eine
 * ausgehende Mail einem Kunden zuordnen wollte, bekam einen Kontakt mit der
 * eigenen Adresse angeboten — und die Mails an einen Kunden, den es beim Abruf
 * noch nicht gab, blieben für immer ohne Zuordnung.
 *
 * Welche Seite die Gegenstelle ist, steht an der gespeicherten Nachricht:
 * `direction`. Die Liste der eigenen Postfachadressen wird dafür nicht
 * gebraucht — sie hat beim Einspielen schon entschieden.
 */
import { normalizeEmail } from './email.js';

/** Die Adressfelder einer Nachricht, so wie sie in der Tabelle stehen. */
export interface AddressFields {
  direction?: string | null;
  fromAddress?: string | null;
  /** Mehrere Empfänger stehen mit Komma getrennt in einem Feld. */
  toAddress?: string | null;
  ccAddresses?: string | null;
}

/**
 * Zerlegt ein Adressfeld in einzelne, normalisierte Adressen.
 *
 * `Name <adresse@example.de>` kommt in gespeicherten Kopfzeilen vor, auch wenn
 * der Abruf normalerweise die nackte Adresse ablegt. Steht ein Name davor,
 * zählt nur, was in den spitzen Klammern steht.
 */
export function splitAddresses(value: string | null | undefined): string[] {
  if (!value) return [];
  return value
    .split(/[,;]/)
    .map((teil) => {
      const klammer = /<([^>]+)>/.exec(teil);
      return normalizeEmail(klammer?.[1] ?? teil);
    })
    .filter((a): a is string => a !== null && a.includes('@'));
}

/**
 * Die Adressen der Gegenseite, in der Reihenfolge ihrer Verbindlichkeit.
 *
 * Bei ausgehender Post die Empfänger — der Hauptempfänger vor dem Cc, damit
 * der Verteiler nicht den Adressaten schlägt. Bei eingehender der Absender.
 */
export function counterpartAddresses(msg: AddressFields): string[] {
  if (msg.direction === 'outbound') {
    return [...splitAddresses(msg.toAddress), ...splitAddresses(msg.ccAddresses)];
  }
  return splitAddresses(msg.fromAddress);
}

/** Die eine Adresse, die eine Nachricht dem Kunden zuordnet. */
export function counterpartAddress(msg: AddressFields): string | null {
  return counterpartAddresses(msg)[0] ?? null;
}

/**
 * Alle an einer Nachricht beteiligten Adressen.
 *
 * Für die Suche nach offener Post zu einem Kontakt: Ob er im Absender, im
 * Empfänger oder im Cc steht, macht für die Zugehörigkeit keinen Unterschied.
 */
export function messageAddresses(msg: AddressFields): string[] {
  return [
    ...splitAddresses(msg.fromAddress),
    ...splitAddresses(msg.toAddress),
    ...splitAddresses(msg.ccAddresses),
  ];
}
